import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { map, Observable, tap, throwError } from 'rxjs';
import { Historic, Valve } from '../models';
import { AuthService } from '@core/index';
import { Role } from '@shared/enums/roles.enum';

@Injectable({
  providedIn: 'root',
})
export class ValveService {
  private http: HttpClient = inject(HttpClient);
  private authService = inject(AuthService);

  public currentValvePagination: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
  } = { pageNumber: 1, pageSize: 50, totalCount: 0 };

  public currentHistoricalPagination: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
  } = { pageNumber: 1, pageSize: 50, totalCount: 0 };

  public resetPagination(): void {
    this.currentValvePagination = {
      pageNumber: 1,
      pageSize: 50,
      totalCount: 0,
    };
  }

  // CRUD
  public getAll(params: { statusId: string }): Observable<Valve[]> {
    return this.http
      .get<{ valves: Valve[]; totalRecords: number }>(
        `${environment.api.root}/Valves`,
        {
          params: { ...this.currentValvePagination, statusId: params.statusId },
        }
      )
      .pipe(
        tap((response) => {
          this.currentValvePagination.totalCount = response.totalRecords;
        }),
        map((response) => {
          return response.valves;
        })
      );
  }

  public get(valveId: string): Observable<Valve> {
    return this.http.get<Valve>(`${environment.api.root}/Valves/${valveId}`);
  }

  public getFile(valveId: string): Observable<Blob> {
    return this.http.get(`${environment.api.root}/Valves/Download/${valveId}`, {
      responseType: 'blob',
    });
  }

  public create(valve: Valve, file?: File): Observable<void> {
    Object.keys(valve).forEach((item: string) => {
      const key = item as keyof typeof valve;

      if (valve[key] === null || valve[key] === undefined) {
        delete valve[key];
      }
    });

    const formData: FormData = this.getFormData(valve, file);
    return this.http.post<void>(
      `${environment.api.root}/Valves/Create`,
      formData
    );
  }

  public update(valve: Valve): Observable<void> {
    Object.keys(valve).forEach((item: string) => {
      const key = item as keyof typeof valve;

      if (valve[key] === null || valve[key] === undefined) {
        delete valve[key];
      }
    });

    return this.http.put<void>(`${environment.api.root}/valve`, valve);
  }

  // Action
  public publish(valveId: string): Observable<void> {
    if (this.authService.haveRole(Role.publisher)) {
      return this.http.put<void>(
        `${environment.api.root}/Valves/Publish/${valveId}`,
        {}
      );
    }

    return throwError(() => new Error('ERR_ROLE'));
  }

  public unpublish(valveId: string): Observable<void> {
    if (this.authService.haveRole(Role.publisher)) {
      return this.http.put<void>(
        `${environment.api.root}/Valves/Unpublish/${valveId}`,
        {}
      );
    }

    return throwError(() => new Error('ERR_ROLE'));
  }

  public archive(valveId: string): Observable<void> {
    if (this.authService.haveRole(Role.publisher)) {
      return this.http.put<void>(
        `${environment.api.root}/Valves/Archive/${valveId}`,
        {}
      );
    }

    return throwError(() => new Error('ERR_ROLE'));
  }

  public getStampInformations(url: string): Observable<string> {
    return this.http.get(`${environment.api.root}/Valves/Download`, {
      responseType: 'text',
      params: { url },
    });
  }

  // Historic
  public getHistorical(): Observable<Historic[]> {
    return this.http
      .get<{ historical: Historic[]; totalRecords: number }>(
        `${environment.api.root}/Historical`,
        { params: { ...this.currentHistoricalPagination } }
      )
      .pipe(
        tap((response) => {
          this.currentHistoricalPagination.totalCount = response.totalRecords;
        }),
        map((response) => {
          return response.historical;
        })
      );
  }

  public exportHistorical(): Observable<Blob> {
    return this.http.get(`${environment.api.root}/ExportHistorical`, {
      responseType: 'blob',
    });
  }

  // Utils

  private getFormData(valve: Valve, file?: File): FormData {
    const formData: FormData = new FormData();

    Object.keys(valve).forEach((key: string) => {
      const localKey = key as keyof typeof valve;
      const value = valve[localKey];

      if (value !== undefined && value !== null) {
        if (typeof value === 'number') {
          formData.append(key, value.toString());
        } else if (typeof value === 'string') {
          formData.append(key, value);
        }
      }
    });

    if (file) {
      formData.append('file', file);
    }

    return formData;
  }
}
